/**
 * Parses a persisted agent `inputShape` (an `Ty.displayTy` string such as
 * `string` or `STRUCT({ name: string, count: int! })`) into a small view-model
 * the agent node editor can render as a friendly list of input fields. We parse
 * the display string rather than the structured type because that's all the
 * node carries client-side — `workflow_nodes.input_shape` stores the display
 * form, computed server-side when the node is created/saved.
 */

export type AgentInputField = {
  readonly name: string;
  readonly type: string;
  /** Non-nullable (`!` in the display string) → the agent expects a value. */
  readonly required: boolean;
};

export type AgentInputShape =
  | { readonly kind: 'struct'; readonly fields: AgentInputField[] }
  | { readonly kind: 'scalar'; readonly type: string }
  | null;

/** Friendlier labels for era type names, for a non-engineer audience. */
function friendlyType(raw: string): string {
  const t = raw.trim();
  switch (t) {
    case 'string':
      return 'text';
    case 'int':
    case 'float':
      return 'number';
    case 'boolean':
      return 'true/false';
    case 'timestamp':
      return 'date';
    default:
      if (t.startsWith('ARRAY(')) {
        return 'list';
      }
      if (t.startsWith('STRUCT(') || t.startsWith('RECORD(')) {
        return 'object';
      }
      if (t.startsWith('ENUM(')) {
        return 'option';
      }
      return t;
  }
}

/**
 * Splits a comma-separated list at the top nesting level only, so commas inside
 * `STRUCT(...)`, `ARRAY(...)`, quotes, etc. don't split a field in two.
 */
function splitTopLevel(input: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let start = 0;
  let quote: string | null = null;
  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (quote !== null) {
      if (ch === quote && input[i - 1] !== '\\') {
        quote = null;
      }
      continue;
    }
    if (ch === "'" || ch === '"') {
      quote = ch;
    } else if (ch === '(' || ch === '{' || ch === '[') {
      depth++;
    } else if (ch === ')' || ch === '}' || ch === ']') {
      depth--;
    } else if (ch === ',' && depth === 0) {
      parts.push(input.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(input.slice(start));
  return parts.map((part) => part.trim()).filter((part) => part.length > 0);
}

/** Parses a single `name: type` struct entry (the name may be quoted). */
function parseField(part: string): AgentInputField | null {
  const trimmed = part.trim();
  let name: string;
  let rest: string;
  // `escapeStructKey` wraps any non-`[a-zA-Z_]` key (e.g. one with digits or a
  // space) in SINGLE quotes; identifier-only keys are bare. Handle both quote
  // styles so the key isn't shown with stray quotes / mis-split on an inner `:`.
  const quote = trimmed[0];
  if (quote === '"' || quote === "'") {
    let end = 1;
    while (
      end < trimmed.length &&
      !(trimmed[end] === quote && trimmed[end - 1] !== '\\')
    ) {
      end++;
    }
    name = trimmed.slice(1, end).replaceAll(`\\${quote}`, quote);
    rest = trimmed.slice(end + 1).replace(/^\s*:\s*/, '');
  } else {
    const colon = trimmed.indexOf(':');
    if (colon < 0) {
      return null;
    }
    name = trimmed.slice(0, colon).trim();
    rest = trimmed.slice(colon + 1).trim();
  }
  if (name.length === 0 || rest.length === 0) {
    return null;
  }
  const required = rest.endsWith('!');
  const type = required ? rest.slice(0, -1).trim() : rest;
  return { name, type: friendlyType(type), required };
}

export function parseAgentInputShape(
  inputShape: string | null | undefined
): AgentInputShape {
  if (inputShape === null || inputShape === undefined) {
    return null;
  }
  const trimmed = inputShape.trim();
  if (trimmed === '') {
    return null;
  }
  const withoutBang = trimmed.endsWith('!')
    ? trimmed.slice(0, -1).trim()
    : trimmed;
  const structMatch = /^STRUCT\(\{([\s\S]*)\}\)$/.exec(withoutBang);
  if (structMatch !== null) {
    const inner = structMatch[1] ?? '';
    const fields = splitTopLevel(inner.trim())
      .map(parseField)
      .filter((field): field is AgentInputField => field !== null);
    return { kind: 'struct', fields };
  }
  return { kind: 'scalar', type: friendlyType(withoutBang) };
}
